import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useConsentStore } from '../stores/consentStore';
import { Card, PageShell } from '../components/ui';

export default function PrivacyPage() {
  const { t } = useTranslation();
  const { consentGiven, analytics, advertising, resetConsent } = useConsentStore();

  return (
    <PageShell maxWidth="3xl" mainClassName="py-10">
      <h1 className="mb-8 text-3xl font-bold text-slate-900 dark:text-white">{t('legal.privacy.title')}</h1>

      <Card className="prose max-w-none space-y-6 p-8 text-sm leading-relaxed text-slate-600 dark:prose-invert dark:text-slate-300">
        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section1.title')}</h2>
          <p>{t('legal.privacy.section1.intro')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li><Trans i18nKey="legal.privacy.section1.items.account" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section1.items.learning" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section1.items.srs" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section1.items.payment" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section1.items.usage" components={{ strong: <strong /> }} /></li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section2.title')}</h2>
          <p>{t('legal.privacy.section2.intro')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>{t('legal.privacy.section2.items.personalized')}</li>
            <li>{t('legal.privacy.section2.items.results')}</li>
            <li>{t('legal.privacy.section2.items.notifications')}</li>
            <li>{t('legal.privacy.section2.items.improve')}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section3.title')}</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li><Trans i18nKey="legal.privacy.section3.items.active" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section3.items.inactive" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section3.items.session" components={{ strong: <strong /> }} /></li>
            <li><Trans i18nKey="legal.privacy.section3.items.logs" components={{ strong: <strong /> }} /></li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section4.title')}</h2>
          <p>{t('legal.privacy.section4.intro')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <Trans
                i18nKey="legal.privacy.section4.access"
                components={{
                  strong: <strong />,
                  profileLink: <Link to="/profile" className="text-blue-600 hover:underline dark:text-blue-400" />,
                }}
              />
            </li>
            <li>
              <Trans i18nKey="legal.privacy.section4.rectification" components={{ strong: <strong /> }} />
            </li>
            <li>
              <Trans
                i18nKey="legal.privacy.section4.erasure"
                components={{
                  strong: <strong />,
                  profileLink: <Link to="/profile" className="text-blue-600 hover:underline dark:text-blue-400" />,
                }}
              />
            </li>
            <li>
              <Trans i18nKey="legal.privacy.section4.portability" components={{ strong: <strong /> }} />
            </li>
            <li>
              <Trans
                i18nKey="legal.privacy.section4.withdraw"
                components={{
                  strong: <strong />,
                  privacyLink: <Link to="/privacy" className="text-blue-600 hover:underline dark:text-blue-400" />,
                }}
              />
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section5.title')}</h2>
          <p>{t('legal.privacy.section5.body')}</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section6.title')}</h2>
          <p>{t('legal.privacy.section6.intro')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <Trans
                i18nKey="legal.privacy.section6.stripe"
                components={{
                  strong: <strong />,
                  stripeLink: (
                    <a
                      href="https://stripe.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    />
                  ),
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey="legal.privacy.section6.adsense"
                components={{
                  strong: <strong />,
                  googleLink: (
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    />
                  ),
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey="legal.privacy.section6.turnstile"
                components={{
                  strong: <strong />,
                  cloudflareLink: (
                    <a
                      href="https://www.cloudflare.com/privacypolicy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    />
                  ),
                }}
              />
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section7.title')}</h2>
          <p>{t('legal.privacy.section7.intro')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <Trans
                i18nKey="legal.privacy.section7.essential"
                components={{ strong: <strong />, code: <code /> }}
              />
            </li>
            <li>
              <Trans i18nKey="legal.privacy.section7.analytics" components={{ strong: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="legal.privacy.section7.advertising" components={{ strong: <strong /> }} />
            </li>
          </ul>
          <p className="mt-2">{t('legal.privacy.section7.compliance')}</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section8.title')}</h2>
          <p>{t('legal.privacy.section8.intro')}</p>
          {consentGiven ? (
            <Card variant="subtle" className="mt-3 space-y-2 p-4">
              <p className="text-sm">
                <strong>{t('legal.privacy.section8.analyticsLabel')}</strong>{' '}
                <span className={analytics ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {analytics ? t('legal.privacy.section8.accepted') : t('legal.privacy.section8.rejected')}
                </span>
              </p>
              <p className="text-sm">
                <strong>{t('legal.privacy.section8.advertisingLabel')}</strong>{' '}
                <span className={advertising ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {advertising ? t('legal.privacy.section8.accepted') : t('legal.privacy.section8.rejected')}
                </span>
              </p>
              <button
                type="button"
                onClick={resetConsent}
                className="mt-2 rounded-lg border border-slate-200 bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              >
                {t('legal.privacy.section8.reset')}
              </button>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('legal.privacy.section8.resetNote')}
              </p>
            </Card>
          ) : (
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {t('legal.privacy.section8.notChosen')}
            </p>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('legal.privacy.section9.title')}</h2>
          <p>
            <Trans
              i18nKey="legal.privacy.section9.body"
              components={{
                dpoLink: <a href="mailto:dpo@cloudmaster.app" className="text-blue-600 hover:underline dark:text-blue-400" />,
              }}
            />
          </p>
        </section>

        <p className="border-t border-slate-200 pt-4 text-xs text-slate-400 dark:border-white/10 dark:text-slate-500">
          {t('legal.privacy.lastUpdated')}
        </p>
      </Card>
    </PageShell>
  );
}
